import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../Experience'
import { InteractableMixin } from '../../Utils/InteractableMixin'
import SignalAudio from './SignalAudio'
import SignalscopeUI from './SignalscopeUI'

/**
 * Signalscope — 空を周回する天体の「シグナル」を聴く小さな望遠鏡。
 * クリックで覗き込みビュー（カメラ FOV ズーム + ビネットUI）へ移行し、
 * スコープが追尾中の天体を自動で捉え続ける（公転にも追従する）。
 * ‹ › ボタン / ←→ キーで追尾する天体を切り替え、切替時は次の天体へ滑らかにスイングする。
 * クリック（切替ボタン以外）か ESC で戻る。
 *
 * プロップはプロシージャルなプレースホルダー（Group 構成）。GLTF への差し替えは setModel() だけ。
 */
const PLANET_CENTER = new THREE.Vector3(-4, -4, 16)
const ENTER_DURATION = 1.6
const EXIT_DURATION = 1.2
const TRACK_SMOOTHING = 3.5 // 追尾の指数減衰係数。大きいほどキビキビ追う

export default class Signalscope {
  constructor() {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.time = this.experience.time
    this.quality = this.experience.quality
    this.debug = this.experience.debug
    this.camera = this.experience.camera
    this.interactionManager = this.experience.interactionManager

    this.state = 'idle' // 'idle' | 'entering' | 'active' | 'exiting'
    this.trackedIndex = 0

    // 照準判定のコーン角（この範囲で音量が smoothstep 変化。切替スイング中のクロスフェードにも効く）
    this.outerAngleDeg = 9.0
    this.innerAngleDeg = 2.5

    // ゾーン近接によるシグナル変化（トリガーゾーンに近いほど大きく・高く鳴る）
    this.zoneFalloffDeg = 120  // この角度以上ゾーンから離れると最小音量
    this.zoneFloorLevel = 0.5  // ゾーンから遠いときの音量下限（探索性の維持）
    this.zoneRiseCents = 20    // ゾーンに近づくと最大 +20 セント音程が上がる
    this.zoneActiveCents = 30  // Effect 発火中はさらに +30 セント

    // 毎フレーム使い回すスクラッチ
    this.aimDir = new THREE.Vector3(0, 0, -1) // スコープの現在の照準方向（滑らかに天体へ追従）
    this.forward = new THREE.Vector3()
    this.toBody = new THREE.Vector3()

    this.ui = new SignalscopeUI()
    this.ui.onPrev = () => this.cycleTracked(-1)
    this.ui.onNext = () => this.cycleTracked(1)
    this.signalAudio = null // AudioManager 生成後（初回 enter 時）に用意する

    this.setModel()
    this.setInteraction()
    this.setExitListeners()
    this.setDebug()

    this.time.on('tick', () => {
      this.update()
    })
  }

  get bodies() {
    return this.experience.world?.orbitingCelestials?.bodies ?? []
  }

  setModel() {
    this.group = new THREE.Group()

    // ダウンロードした望遠鏡モデル（Signalscope は loadingScreen 'completed' 後に
    // 生成されるため、リソースは同期的に取得できる）
    this.model = this.experience.resources.items.telescopeModel.scene

    // 原寸が不明なので、高さ 0.7 ユニットに正規化し、足元を group 原点（惑星表面）に合わせる
    const bounds = new THREE.Box3().setFromObject(this.model)
    const size = bounds.getSize(new THREE.Vector3())
    const modelScale = 0.7 / size.y
    this.model.scale.setScalar(modelScale)
    const center = bounds.getCenter(new THREE.Vector3())
    this.model.position.set(-center.x * modelScale, -bounds.min.y * modelScale, -center.z * modelScale)
    this.model.rotation.y = 1.3684 // 焚き火・惑星の方を向かせる（debug GUI で調整した値）
    this.group.add(this.model)

    // レイキャスト用の不可視ヒットスフィア（InteractionManager は this.mesh のみ判定する）
    this.mesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 8, 8),
      new THREE.MeshBasicMaterial({ visible: false })
    )
    this.mesh.position.y = 0.35
    this.group.add(this.mesh)

    this.setSurfacePosition(new THREE.Vector3(-4.95, -1.3, 17.65))
    this.scene.add(this.group)
  }

  setSurfacePosition(position) {
    this.group.position.copy(position)
    const normal = position.clone().sub(PLANET_CENTER).normalize()
    this.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal)
  }

  setInteraction() {
    Object.assign(this, InteractableMixin)

    this.onInteract = () => {
      this.enter()
    }

    this.makeInteractable()
  }

  setExitListeners() {
    window.addEventListener('keydown', (event) => {
      if (this.state !== 'active' && this.state !== 'entering') return

      if (event.key === 'Escape') {
        this.exit()
      } else if (event.key === 'ArrowLeft') {
        this.cycleTracked(-1)
      } else if (event.key === 'ArrowRight') {
        this.cycleTracked(1)
      }
    })

    // クリックで戻る。ただし切替ボタン上のクリックと、ドラッグのリリースは除外
    window.addEventListener('pointerdown', (event) => {
      this.downX = event.clientX
      this.downY = event.clientY
    })
    window.addEventListener('click', (event) => {
      if (this.state !== 'active') return
      if (event.target.closest?.('.scope-nav-button')) return
      if (Math.hypot(event.clientX - this.downX, event.clientY - this.downY) > 5) return
      this.exit()
    })

    // セクションをスクロールで離れたら覗き込みを解く
    document.addEventListener('sectionChanged', () => {
      if (this.state === 'active' || this.state === 'entering') {
        this.exit()
      }
    })
  }

  enter() {
    if (this.state !== 'idle') return
    if (this.bodies.length === 0) return
    if (!this.camera.enterScopeMode(ENTER_DURATION)) return // イントロ中は入れない

    this.state = 'entering'
    this.interactionManager.suppressed = true
    document.body.style.cursor = 'default'

    this.experience.audioManager?.playSE('scopeSwitchSE', 0.6)

    // 照準は現在のカメラ視線から始めて、追尾ループが天体へスイングさせる
    this.camera.instance.getWorldDirection(this.aimDir)
    this.trackedIndex = this.findNearestBodyIndex()

    if (!this.signalAudio && this.experience.audioManager) {
      this.signalAudio = new SignalAudio(this.experience.audioManager)
    }
    this.signalAudio?.start()
    this.ui.show()

    // カメラの FOV tween が終わったら active に
    // （'active' になるまで exit クリックは受け付けないので、enter したクリック自体では閉じない）
    gsap.delayedCall(Math.max(ENTER_DURATION * this.quality.motionScale, 0.3), () => {
      if (this.state === 'entering') {
        this.state = 'active'
      }
    })
  }

  exit() {
    if (this.state !== 'active' && this.state !== 'entering') return

    this.state = 'exiting'
    this.ui.hide()
    this.signalAudio?.stop()

    this.camera.exitScopeMode(() => {
      this.state = 'idle'
      this.interactionManager.suppressed = false
    }, EXIT_DURATION)
  }

  // 現在のカメラ視線に最も近い天体を初期の追尾対象にする
  findNearestBodyIndex() {
    this.camera.instance.getWorldDirection(this.forward)

    let bestIndex = 0
    let bestDot = -Infinity
    this.bodies.forEach((body, index) => {
      body.getWorldPosition(this.toBody).sub(this.camera.instance.position).normalize()
      const dot = this.forward.dot(this.toBody)
      if (dot > bestDot) {
        bestDot = dot
        bestIndex = index
      }
    })
    return bestIndex
  }

  cycleTracked(step) {
    if (this.state !== 'active' && this.state !== 'entering') return
    const count = this.bodies.length
    if (count === 0) return

    this.trackedIndex = (this.trackedIndex + step + count) % count
    this.experience.audioManager?.playSE('scopeSwitchSE', 0.6)
  }

  update() {
    if (this.state !== 'active' && this.state !== 'entering') return

    const bodies = this.bodies
    if (bodies.length === 0) return

    const cameraInstance = this.camera.instance
    const tracked = bodies[this.trackedIndex % bodies.length]

    // --- 自動追尾: 照準方向を追尾中の天体へ指数減衰で寄せる ---
    tracked.getWorldPosition(this.toBody).sub(cameraInstance.position).normalize()
    if (this.quality.reducedMotion) {
      this.aimDir.copy(this.toBody)
    } else {
      const deltaSeconds = Math.min(this.time.delta, 100) * 0.001
      const alpha = 1 - Math.exp(-TRACK_SMOOTHING * deltaSeconds)
      this.aimDir.lerp(this.toBody, alpha).normalize()
    }
    // controls.target をカメラ直前(0.6)に置いて視線を照準方向へ向ける
    this.camera.controls.target.copy(cameraInstance.position).addScaledVector(this.aimDir, 0.6)

    // --- シグナル音量: 実際の視線と各天体の角度差から算出（切替時は自然にクロスフェード） ---
    if (!this.signalAudio) return
    cameraInstance.getWorldDirection(this.forward)

    const outerDot = Math.cos(THREE.MathUtils.degToRad(this.outerAngleDeg))
    const innerDot = Math.cos(THREE.MathUtils.degToRad(this.innerAngleDeg))

    const orbitingCelestials = this.experience.world.orbitingCelestials
    const falloffDot = Math.cos(THREE.MathUtils.degToRad(this.zoneFalloffDeg))

    let trackedLevel = 0
    for (const body of bodies) {
      body.getWorldPosition(this.toBody).sub(cameraInstance.position).normalize()
      const dot = this.forward.dot(this.toBody)
      const aimLevel = THREE.MathUtils.smoothstep(dot, outerDot, innerDot)

      // ゾーンに近いほど大きく（エフェクト発火位置で最大）。遠くても floor まで
      const proximity = orbitingCelestials.getZoneProximity(body, falloffDot)
      const level = aimLevel * (this.zoneFloorLevel + (1 - this.zoneFloorLevel) * proximity)

      // 波長: 近づくにつれ僅かに上がり、Effect 発火中（inZone）はさらに一段上がる
      const detune = proximity * this.zoneRiseCents + (body.inZone ? this.zoneActiveCents : 0)

      this.signalAudio.setLevel(body.name, level, detune)
      if (body === tracked) trackedLevel = level
    }

    // 表示は常に「追尾中の天体」（スイング中は強度バーが下がる）
    this.ui.setSignal({ name: tracked.name, level: trackedLevel })
  }

  setDebug() {
    if (!this.debug.active) return

    this.debugFolder = this.debug.ui.addFolder('Signalscope')
    const apply = () => this.setSurfacePosition(this.group.position)
    this.debugFolder.add(this.group.position, 'x').min(-10).max(5).step(0.05).name('Position X').onChange(apply)
    this.debugFolder.add(this.group.position, 'y').min(-8).max(2).step(0.05).name('Position Y').onChange(apply)
    this.debugFolder.add(this.group.position, 'z').min(12).max(20).step(0.05).name('Position Z').onChange(apply)
    this.debugFolder.add(this.group.scale, 'x').min(0.2).max(3).step(0.05).name('Scale').onChange((value) => {
      this.group.scale.setScalar(value)
    })
    this.debugFolder.add(this.model.rotation, 'y').min(-Math.PI).max(Math.PI).step(0.01).name('Facing (rotate Y)')
    this.debugFolder.add(this, 'outerAngleDeg').min(2).max(30).step(0.5).name('Outer Angle (deg)')
    this.debugFolder.add(this, 'innerAngleDeg').min(0.5).max(15).step(0.5).name('Inner Angle (deg)')
    this.debugFolder.add(this, 'zoneFalloffDeg').min(30).max(180).step(5).name('Zone Falloff (deg)')
    this.debugFolder.add(this, 'zoneFloorLevel').min(0).max(1).step(0.05).name('Zone Floor Level')
    this.debugFolder.add(this, 'zoneRiseCents').min(0).max(100).step(5).name('Zone Rise (cents)')
    this.debugFolder.add(this, 'zoneActiveCents').min(0).max(200).step(5).name('Effect Detune (cents)')
    this.debugFolder.add({ enter: () => this.enter() }, 'enter').name('🔭 Enter Scope')
    this.debugFolder.add({ exit: () => this.exit() }, 'exit').name('↩️ Exit Scope')
    this.debugFolder.add({ next: () => this.cycleTracked(1) }, 'next').name('⏭ Next Target')
    this.debugFolder.add(this, 'state').name('State').listen()
    this.debugFolder.close()
  }
}
